// Structural types only — this module is consumed by the alias-free test
// runner, so it must not import from "@/lib/*". Real Book/BookDetail values
// are structurally compatible.

export interface KnowledgeLinkedBook {
  knowledge_bases: readonly string[];
}

export interface SpineChapterLike {
  id: string;
  content_type: string;
  page_ids: readonly string[];
}

export interface BookPageLike {
  id: string;
  blocks: ReadonlyArray<{ type: string; payload?: unknown }>;
}

export interface BookDetailLike {
  spine: { chapters: readonly SpineChapterLike[] } | null;
  pages: readonly BookPageLike[];
}

export interface KnowledgeScopeGroup<T> {
  key: string;
  isUncategorized: boolean;
  items: T[];
}
export function distinctKnowledge<T>(
  items: readonly T[],
  getKnowledgeBases: (item: T) => readonly string[] | null | undefined,
): string[] {
  const names = new Set<string>();

  for (const item of items) {
    for (const value of getKnowledgeBases(item) ?? []) {
      const name = value.trim();
      if (name) names.add(name);
    }
  }

  return Array.from(names).sort((left, right) => left.localeCompare(right));
}

export function selectKnowledgeScope(
  availableKnowledge: readonly string[],
  requestedKnowledge: string | null,
): string | null {
  if (!requestedKnowledge) return null;
  const name = requestedKnowledge.trim();
  return availableKnowledge.includes(name) ? name : null;
}

export function filterItemsByKnowledge<T>(
  items: readonly T[],
  activeKnowledge: string | null,
  getKnowledgeBases: (item: T) => readonly string[] | null | undefined,
): readonly T[] {
  if (!activeKnowledge) return items;
  const knowledge = activeKnowledge.trim();
  if (!knowledge) return items;
  return items.filter((item) =>
    (getKnowledgeBases(item) ?? []).some(
      (candidate) => candidate.trim() === knowledge,
    ),
  );
}

export function filterBooksByKnowledge<T extends KnowledgeLinkedBook>(
  books: readonly T[],
  activeKnowledge: string | null,
): readonly T[] {
  return filterItemsByKnowledge(
    books,
    activeKnowledge,
    (book) => book.knowledge_bases,
  );
}

export function selectKnowledgeGroups<
  T,
  Group extends KnowledgeScopeGroup<T>,
>(
  groups: readonly Group[],
  activeKnowledge: string | null,
): readonly Group[] {
  if (!activeKnowledge) return groups;
  return groups.filter(
    (group) => !group.isUncategorized && group.key === activeKnowledge,
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function resolveKnowledgePointPage(
  detail: BookDetailLike,
  knowledgePointId: string | null,
): string | null {
  if (!knowledgePointId || !detail.spine) return null;

  // Fast path — in the current book pipeline the mastery-map KP id IS the
  // spine chapter id (e.g. "ch_a74e243164"). A direct id join needs no
  // concept-graph lookup.
  const directChapter = detail.spine.chapters.find(
    (chapter) => chapter.id === knowledgePointId,
  );
  const directPageId = directChapter?.page_ids[0];
  if (
    directPageId &&
    detail.pages.some((candidate) => candidate.id === directPageId)
  ) {
    return directPageId;
  }

  const overviewChapter = detail.spine.chapters.find(
    (chapter) => chapter.content_type === "overview",
  );
  if (!overviewChapter) return null;

  const overviewPageIds = new Set(overviewChapter.page_ids);
  for (const page of detail.pages) {
    if (!overviewPageIds.has(page.id)) continue;

    for (const block of page.blocks) {
      if (block.type !== "concept_graph") continue;
      const payload = asRecord(block.payload);
      const index = asRecord(payload?.index);
      const nodeToChapter = asRecord(index?.node_to_chapter);
      const chapterId = nodeToChapter?.[knowledgePointId];
      if (typeof chapterId !== "string" || !chapterId) continue;

      const chapter = detail.spine.chapters.find(
        (candidate) => candidate.id === chapterId,
      );
      const pageId = chapter?.page_ids[0];
      if (pageId && detail.pages.some((candidate) => candidate.id === pageId)) {
        return pageId;
      }
    }
  }

  return null;
}
